import { useEffect, useState } from 'react'
import { errorLogger } from '../../../util'

const MAX_TOTAL_CALL_ATTEMPTS = 3

// This function can be different for repos: school-store-ui/package-browse-ui/product-browse-ui
const sendReportError = (faultDetails) => {
    errorLogger(faultDetails, null, false)
}

// Comment: Checks whether API response is empty (array or object)
const isEmptyResponse = (result) => {
    if (Array.isArray(result)) {
        return result.length === 0
    }

    if (result && typeof result === 'object') {
        return Object.keys(result).length === 0
    }

    return true
}

// Comment: Normalizes error object for logging
const normalizeError = (err) => ({
    message: err?.message,
    stack: err?.stack,
})

// Comment: Promise-based delay helper
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// Comment: Builds final request URL with host and cachebuster
const buildRequestUrl = (apiUrl) => {
    const host = window.location.hostname === 'localhost'
        ? window.location.origin 
        : process.env.REACT_APP_API_PATH

    const url = new URL(`${host}${apiUrl}`, host)
    url.searchParams.set('_cb', Date.now())
    return url.toString()

}

// Comment: Handles fetch response and returns parsed data
const handleResponse = async (response, apiUrl, attempt) => {
    let responseText

    if (!response.ok) {
        try {
            responseText = await response.text()
        } catch {}

        sendReportError({
            apiUrl,
            errorType: 'http_error',
            attempt,
            status: response.status,
            statusText: response.statusText,
            responseText: responseText?.slice(0, 1000),
        })

        return null
    }

    let result
    try {
        responseText = await response.text()
        result = JSON.parse(responseText)
    } catch {
        sendReportError({
            apiUrl,
            errorType: 'invalid_json',
            attempt,
            responseText: responseText?.slice(0, 1000),
        })

        return null
    }

    if (isEmptyResponse(result)) {
        sendReportError({
            apiUrl,
            errorType: 'empty_response',
            attempt,
            responseText: responseText?.slice(0, 1000),
        })

        return null
    }

    return result
}

// Comment: Logs error for a specific attempt
const logAttemptError = (error, apiUrl, attempt) => {
    if (error.name === 'TimeoutError') {
        sendReportError({ apiUrl, errorType: 'timeout', attempt, error: normalizeError(error) })
    } else if (error.name === 'AbortError') {
        sendReportError({ apiUrl, errorType: 'abort', attempt, error: normalizeError(error) })
    } else {
        sendReportError({ apiUrl, errorType: 'network_error', attempt, error: normalizeError(error) })
    }
}

export const useFetchCustomerData = (apiUrl) => {
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(null)

    useEffect(() => {
        (async () => {
            try {
                for (let attempt = 1; attempt <= MAX_TOTAL_CALL_ATTEMPTS; attempt++) {
                    try {
                        const response = await fetch(
                            buildRequestUrl(apiUrl),
                            { signal: AbortSignal.timeout(5000) }
                        )
                        const result = await handleResponse(response, apiUrl, attempt)

                        if (result) {
                            setData(result)
                        }
                        return

                    } catch (error) {
                        logAttemptError(error, apiUrl, attempt)

                        if (attempt === MAX_TOTAL_CALL_ATTEMPTS) {
                            console.error('[useFetchCustomerData]', apiUrl, error)
                            return
                        }

                        // Comment: Wait 200ms before next retry attempt
                        await delay(200)
                    }
                }
            } finally {
                setLoading(false)
            }
        })()
    }, [apiUrl])

    return { data, loading }
}
